import React, { useEffect, useState, useContext, useCallback } from "react";

import { useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "../component/Footer";
import HomeShopList from "../component/home/HomeShopList";
import HomeFilterDrawer from "../component/home/HomeFilterDrawer";
import RegularProductListing from "../component/RegularProductListing";
import MixedProductCarousel from "../component/MixedProductCarousel";
import TopFeedback from "../component/TopFeedback";
import { Row, Modal } from "antd";
// Home Hero Components
import CategoryHeroSlider from "../component/CategoryHeroSlider";
import HomeBanner from "../component/home/HomeBanner";
import CategorySwiper from "../component/CategorySwiper";
import DealsSection from "../component/DealsSection";
import CategoryFeaturedBanners from "../component/CategoryFeaturedBanners";
import CategoryProductListing from "../component/CategoryProductListing";
import ChangeLocationContent from "../component/ChangeLocationContent";
import { LocationContext } from "../LocationContext";
import useFilters from "../hooks/useFilters";
import BrandsServ from "../service/BrandsServ";
import { useCategory } from "../context/CategoryContext";
import { usePreference } from "../context/PreferenceContext";
import "../css/shopPage.css";

const Home = () => {
    const { t } = usePreference();
    const location = useLocation();
    const {
        latitude, setLatitude,
        longitude, setLongitude,
        radius, setRadius,
        setSelectedAddress,
        setCityName,
        setIsManualLocation
    } = useContext(LocationContext);

    // Filter State
    const { filters, debouncedFilters, updateFilter, resetFilters: resetFilterState } = useFilters({
        search: '',
        zipcode: '',
        categories: [],
        brands: [],
        rating: 0,
        sort: 'name-asc',
    });

    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [brands, setBrands] = useState([]);
    const [hasCarousel, setHasCarousel] = useState(true);
    const [hasBanner, setHasBanner] = useState(true);
    const [isLocationPromptVisible, setIsLocationPromptVisible] = useState(false);
    const [shopCount, setShopCount] = useState(null);

    // Fetch filter options (Categories are now handled globally)
    const { categories } = useCategory();

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const brandRes = await BrandsServ.index({ limit: "all" });
                setBrands(brandRes.data || []);
            } catch (err) {
                console.error("Error fetching filter options", err);
            }
        };
        fetchOptions();
    }, []);

    // Check if user has confirmed location on first visit
    useEffect(() => {
        const hasConfirmed = localStorage.getItem("hasConfirmedLocation");
        if (!hasConfirmed) {
            setIsLocationPromptVisible(true);
        }
    }, []);

    const handleLocationPromptApply = useCallback((data) => {
        let addressText = "";
        let cityText = "";
        if (typeof data === "string") addressText = data;
        else if (typeof data === "object") {
            addressText = data.address || data.place_name || data.formatted_address || data.name || data.label || JSON.stringify(data);
            cityText = data.city || "";
            if (data.lat && data.lng) {
                setLatitude(data.lat);
                setLongitude(data.lng);
                setIsManualLocation(true);
            }
            if (data.radius) {
                setRadius(data.radius);
            }
        }
        setSelectedAddress(addressText);
        setCityName(cityText);
        localStorage.setItem("hasConfirmedLocation", "true");
        setIsLocationPromptVisible(false);
    }, [setLatitude, setLongitude, setIsManualLocation, setRadius, setSelectedAddress, setCityName]);

    const handleLocationPromptCancel = useCallback(() => {
        localStorage.setItem("hasConfirmedLocation", "true"); // assume they are fine with auto-detected location if they cancel
        setIsLocationPromptVisible(false);
    }, []);

    // Filter state already dynamically applies. Removing auto-close logic so user can select multiple filters without interruption.

    // Sync URL search
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const searchParam = params.get("search");
        if (searchParam) {
            updateFilter('search', searchParam);
        }
    }, [location.search]);

    // Listen for window events
    useEffect(() => {
        const handleToggleFilter = () => setMobileFiltersOpen(true);
        const handleOpenPrompt = () => setIsLocationPromptVisible(true);

        window.addEventListener('toggle-global-filter', handleToggleFilter);
        window.addEventListener('open-location-prompt', handleOpenPrompt);

        return () => {
            window.removeEventListener('toggle-global-filter', handleToggleFilter);
            window.removeEventListener('open-location-prompt', handleOpenPrompt);
        };
    }, []);

    const handleReset = useCallback(() => {
        resetFilterState();
        setLatitude(null);
        setLongitude(null);
        setRadius(7);
        setSelectedAddress("");
    }, [resetFilterState, setLatitude, setLongitude, setRadius, setSelectedAddress]);

    // Handle scroll to section
    useEffect(() => {
        if (location.state?.scrollTo === "shops-section") {
            setTimeout(() => {
                const el = document.getElementById("shops-section");
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 300);
        }
    }, [location.state]);

    // Dispatch event for Navbar to know if we have shops
    useEffect(() => {
        // If we are on home page, notify Navbar if there are shops
        if (location.pathname === '/') {
            const event = new CustomEvent('set-has-shops', { detail: shopCount > 0 || shopCount === null });
            window.dispatchEvent(event);
        }
    }, [shopCount, location.pathname]);

    return (
        <div className="home-page">
            <Navbar />

            <Modal
                title={null}
                open={isLocationPromptVisible}
                onCancel={handleLocationPromptCancel}
                footer={null}
                destroyOnHidden
                centered
                maskClosable={false}
                className="initial-location-modal"
            >
                <ChangeLocationContent 
                    onApply={handleLocationPromptApply} 
                    onReset={handleReset} 
                    isOpen={isLocationPromptVisible} 
                />
            </Modal>

            <HomeFilterDrawer
                open={mobileFiltersOpen}
                onClose={() => setMobileFiltersOpen(false)}
                onReset={handleReset}
                filters={filters}
                onFilterChange={updateFilter}
                categories={categories.map(c => ({ id: c._id || c.id, name: c.name, parentId: c.parentId?._id || c.parentId }))}
                brands={brands.map(b => ({ id: b._id || b.id, name: b.name }))}
            />

            {/* 🔥 Consolidated Home Sections */}
            <div className="shop-hero-section">
                {shopCount !== 0 && (
                    <>
                        <Row style={{
                            background: "darkgreen",
                            padding: "30px 0",
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                            <div className="shop-hero-item with-margin home-hero-wrapper" style={{
                                width: "100%",
                                objectFit: "cover",
                                borderRadius: "12px"
                            }} >
                                <div className={`home-hero-split ${!hasCarousel ? 'no-carousel' : ''} ${!hasBanner ? 'no-banner' : ''}`}>
                                    {hasCarousel && (
                                        <div className="home-hero-left">
                                            <CategoryHeroSlider onItemsLoaded={setHasCarousel} />
                                        </div>
                                    )}
                                    {hasBanner && (
                                        <div className="home-hero-right">
                                            <HomeBanner onBannerLoaded={setHasBanner} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Row>
                        {shopCount !== 0 && (
                            <div className="shop-hero-item">
                                <CategorySwiper />
                            </div>
                        )}
                        <div className="shop-hero-item">
                            <DealsSection type="today" title={t('today_hot_deals')} icon="⚡" filters={debouncedFilters} />
                        </div>
                    </>
                )}
                
                <div className="shop-hero-item" id="shops-section">
                    <HomeShopList
                        filters={debouncedFilters}
                        latitude={latitude}
                        longitude={longitude}
                        radius={radius}
                        onReset={handleReset}
                        categories={categories}
                        onShopsLoaded={(count) => setShopCount(count)}
                    />
                </div>
                
                {shopCount !== 0 && (
                    <>
                        <div className="shop-hero-item">
                            <CategoryFeaturedBanners />
                        </div>
                        <div className="shop-hero-item">
                            <CategoryProductListing filters={debouncedFilters} />
                        </div>
                        <div className="shop-hero-item">
                            <RegularProductListing filters={debouncedFilters} />
                        </div>

                        <div className="shop-hero-item">
                            <MixedProductCarousel title={t('products_you_may_like')} />
                        </div>
                        <div className="shop-hero-item">
                            <TopFeedback latitude={latitude} longitude={longitude} radius={radius} />
                        </div>
                    </>
                )}
            </div>


            <Footer />

            <style>{`
                @media (max-width: 768px) {
                    .mobile-filter-btn {
                        display: flex !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default Home;
